import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { Bell, CheckCheck, Loader2, MailOpen } from "lucide-react";
import { toast } from "sonner";
import {
  listarNotificaciones,
  marcarLeida,
  marcarTodasLeidas,
  etiquetaTipo,
} from "@/data/socioNotifsStore";
import { tiempoRelativo } from "@/data/saldoSocioStore";

const ALTO_MIN_LISTA = 40;

// Alto que deja la ventana a la lista entre su borde superior y la reserva inferior
// del layout (barra de navegación), para que sólo la lista se desplace.
function useAltoHastaNav(el) {
  const [alto, setAlto] = useState(null);
  useLayoutEffect(() => {
    if (!el) return undefined;
    const main = el.closest("main");
    const medir = () => {
      const reserva = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const top = el.getBoundingClientRect().top + window.scrollY;
      setAlto(Math.max(ALTO_MIN_LISTA, Math.floor(window.innerHeight - top - reserva)));
    };
    medir();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(medir) : null;
    if (ro) {
      ro.observe(document.body);
      if (main) ro.observe(main);
    }
    window.addEventListener("resize", medir);
    window.visualViewport?.addEventListener("resize", medir);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", medir);
      window.visualViewport?.removeEventListener("resize", medir);
    };
  }, [el]);
  return alto;
}

/**
 * Pantalla dedicada de notificaciones del socio.
 *
 * Muestra el historial completo (hasta 500 elementos) ordenado del
 * más reciente al más antiguo. Cada tarjeta permite marcar como leída
 * al hacer click, y hay una acción global "Marcar todas como leídas".
 */
export default function Notificaciones() {
  const [items, setItems] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [marcandoTodas, setMarcandoTodas] = useState(false);
  const [lista, setLista] = useState(null);
  const altoLista = useAltoHastaNav(lista);

  const hidratar = useCallback(async () => {
    setCargando(true);
    try {
      const data = await listarNotificaciones({ limite: 500 });
      setItems(data.items);
      setNoLeidas(data.noLeidas);
    } catch {
      toast.error("No pudimos cargar tus notificaciones.");
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    hidratar();
  }, [hidratar]);

  const clickItem = async (n) => {
    if (n.leida) return;
    try {
      await marcarLeida(n.id);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, leida: true } : x)));
      setNoLeidas((c) => Math.max(0, c - 1));
    } catch {
      toast.error("No pudimos actualizar la notificación.");
    }
  };

  const clickMarcarTodas = async () => {
    setMarcandoTodas(true);
    try {
      const n = await marcarTodasLeidas();
      setItems((prev) => prev.map((x) => ({ ...x, leida: true })));
      setNoLeidas(0);
      toast.success(
        n > 0
          ? `${n} notificaciones marcadas como leídas.`
          : "No había notificaciones nuevas.",
      );
    } catch {
      toast.error("No pudimos marcar todas como leídas.");
    } finally {
      setMarcandoTodas(false);
    }
  };

  return (
    <div data-testid="screen-notificaciones" className="flex flex-col gap-6 [@media(max-height:480px)]:gap-3">
      <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 [@media(max-height:480px)]:flex-row [@media(max-height:480px)]:items-center [@media(max-height:480px)]:justify-between [@media(max-height:480px)]:gap-2">
        <div>
          <span className="overline text-accent inline-flex items-center gap-2 [@media(max-height:480px)]:hidden">
            <Bell className="w-4 h-4" />
            Portal socio
          </span>
          <h1 className="mt-2 font-heading text-3xl tracking-tight [@media(max-height:480px)]:mt-0 [@media(max-height:480px)]:text-xl">
            Tus notificaciones
          </h1>
          <p className="text-sm text-muted-foreground mt-1 [@media(max-height:480px)]:mt-0 [@media(max-height:480px)]:text-xs">
            {cargando
              ? "Cargando…"
              : items.length === 0
              ? "Aún no tienes notificaciones."
              : `${items.length} en total · ${noLeidas} sin leer`}
          </p>
        </div>
        <button
          type="button"
          onClick={clickMarcarTodas}
          data-testid="notif-screen-marcar-todas"
          disabled={marcandoTodas || noLeidas === 0}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium border hairline bg-card/70 text-foreground hover:bg-muted transition-colors disabled:opacity-50 [@media(max-height:480px)]:shrink-0 [@media(max-height:480px)]:py-2 [@media(max-height:480px)]:text-xs"
        >
          {marcandoTodas ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <CheckCheck className="w-4 h-4" />
          )}
          Marcar todas como leídas
        </button>
      </header>

      {cargando ? (
        <div className="rounded-2xl border hairline bg-card p-8 flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando notificaciones…
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="notif-screen-vacio"
          className="rounded-2xl border hairline bg-card p-10 text-center"
        >
          <MailOpen className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            Cuando el club te envíe una comunicación o cuando tengas actividad
            reciente (compras, cambios de tus eventos), la verás aquí.
          </p>
        </div>
      ) : (
        <ul
          ref={setLista}
          data-testid="notif-screen-lista"
          style={altoLista ? { maxHeight: altoLista } : undefined}
          className="flex flex-col gap-3 overflow-y-auto overscroll-contain -mx-1 px-1 py-1"
        >
          {items.map((n) => (
            <li
              key={n.id}
              data-testid={`notif-screen-item-${n.id}`}
              onClick={() => clickItem(n)}
              className={`rounded-2xl border hairline bg-card p-5 cursor-pointer transition-colors ${
                n.leida
                  ? "bg-card"
                  : "bg-primary/5 dark:bg-accent/10 ring-1 ring-primary/20 dark:ring-accent/30"
              } hover:bg-muted/50`}
            >
              <div className="flex items-center gap-3 mb-1">
                <span className="overline text-accent text-[10px]">
                  {etiquetaTipo(n.tipo)}
                </span>
                {!n.leida ? (
                  <span
                    data-testid={`notif-screen-badge-${n.id}`}
                    className="inline-flex items-center rounded-full border border-primary/40 dark:border-accent/40 bg-primary/10 dark:bg-accent/10 text-primary dark:text-accent px-2 py-0.5 text-[10px] uppercase tracking-wide"
                  >
                    Nueva
                  </span>
                ) : null}
                <span className="ml-auto text-[11px] text-muted-foreground">
                  {tiempoRelativo(n.fechaCreacion)}
                </span>
              </div>
              <p className="font-heading text-lg leading-tight">{n.titulo}</p>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                {n.mensaje}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
